import {
  DEFAULT_ADVANCED_TRAINING_VIDEO_URL,
  DEFAULT_CUSTOMIZE_GUIDE_VIDEO_URL,
  DEFAULT_EDIT_SECTION_VIDEO_URL,
  DEFAULT_NEARBY_VIDEO_URL,
  DEFAULT_SETUP_INTERVIEW_VIDEO_URL,
  configuredVideoUrl,
  youtubeEmbedUrl,
  youtubeVideoId,
} from "@/utils/trainingVideos";

export const HOW_TO_VIDEO_PLACEMENTS = [
  {
    value: "setup",
    label: "Setup widget",
    settingKey: "setupInterviewTrainingVideoUrl",
    fallback: DEFAULT_SETUP_INTERVIEW_VIDEO_URL,
  },
  {
    value: "customize",
    label: "Customize guidebook",
    settingKey: "newOwnerTrainingVideoUrl",
    fallback: DEFAULT_CUSTOMIZE_GUIDE_VIDEO_URL,
  },
  {
    value: "editSection",
    label: "Edit a section",
    settingKey: "editSectionTrainingVideoUrl",
    fallback: DEFAULT_EDIT_SECTION_VIDEO_URL,
  },
  {
    value: "nearby",
    label: "Nearby places",
    settingKey: "nearbyTrainingVideoUrl",
    fallback: DEFAULT_NEARBY_VIDEO_URL,
  },
  {
    value: "advanced",
    label: "Advanced training",
    settingKey: "advancedTrainingVideoUrl",
    fallback: DEFAULT_ADVANCED_TRAINING_VIDEO_URL,
  },
];

export const HOW_TO_VIDEO_PLACEMENT_OPTIONS = [
  { value: "", label: "None (gallery only)" },
  ...HOW_TO_VIDEO_PLACEMENTS.map(({ value, label }) => ({ value, label })),
];

const PLACEMENT_VALUES = new Set(HOW_TO_VIDEO_PLACEMENTS.map((item) => item.value));

const DEFAULT_HOW_TO_VIDEOS = [
  {
    id: "setup",
    title: "Set up your digital guide",
    description:
      "Walk through New Owner Setup, from property type to QR code.",
    placement: "setup",
    tags: ["setup", "new owner", "New Owners"],
  },
  {
    id: "customize",
    title: "Customize your guidebook",
    description:
      "Edit Property Info, publish, and share your guide with guests.",
    placement: "customize",
    tags: ["guidebook", "customize", "New Owners"],
  },
  {
    id: "editSection",
    title: "How to edit a section",
    description:
      "Turn on Edit, format text, add images and YouTube, and use AI Assistant.",
    placement: "editSection",
    tags: ["editing", "New Owners"],
  },
  {
    id: "nearby",
    title: "Set up Nearby places",
    description:
      "Choose categories, set distance, and highlight places guests will love.",
    placement: "nearby",
    tags: ["nearby", "New Owners"],
  },
  {
    id: "advanced",
    title: "Advanced training",
    description:
      "Edit the main menu, add and move pages, and adjust other guidebook settings.",
    placement: "advanced",
    tags: ["Advanced", "menu"],
  },
];

export function emptyHowToVideo() {
  return {
    id: "",
    title: "",
    description: "",
    url: "",
    placement: "",
    tags: [],
  };
}

export function newHowToVideoId() {
  return `video-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

export function normalizeHowToTags(raw) {
  const list = Array.isArray(raw)
    ? raw
    : String(raw || "").split(/[,;]/);
  const seen = new Set();
  const tags = [];
  for (const item of list) {
    const tag = String(item || "")
      .replace(/\s+/g, " ")
      .trim();
    if (!tag) {
      continue;
    }
    const key = tag.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    tags.push(tag);
  }
  return tags;
}

export function normalizeHowToVideo(raw = {}) {
  const placement = String(raw.placement || "").trim();
  return {
    id: String(raw.id || "").trim(),
    title: String(raw.title || "").trim(),
    description: String(raw.description || "").trim(),
    url: String(raw.url || "").trim(),
    placement: PLACEMENT_VALUES.has(placement) ? placement : "",
    tags: normalizeHowToTags(raw.tags),
  };
}

export function placementLabel(placement) {
  const found = HOW_TO_VIDEO_PLACEMENTS.find((item) => item.value === placement);
  return found ? found.label : "Gallery only";
}

export function seedHowToVideos(appSettings = {}) {
  return DEFAULT_HOW_TO_VIDEOS.map((video) => {
    const meta = HOW_TO_VIDEO_PLACEMENTS.find(
      (item) => item.value === video.placement,
    );
    const url = configuredVideoUrl(
      appSettings[meta.settingKey],
      meta.fallback,
    );
    return normalizeHowToVideo({
      ...video,
      url,
    });
  });
}

/**
 * Resolve the how-to video list from settings.
 * - undefined / missing key → seed from existing URL fields + defaults
 * - empty array → no videos (admin cleared them)
 */
export function resolveHowToVideos(appSettings = {}) {
  if (
    !appSettings ||
    !Object.prototype.hasOwnProperty.call(appSettings, "howToVideos")
  ) {
    return seedHowToVideos(appSettings);
  }
  const list = appSettings.howToVideos;
  if (!Array.isArray(list)) {
    return seedHowToVideos(appSettings);
  }
  return list
    .map((raw) => {
      const video = normalizeHowToVideo(raw);
      if (raw && Object.prototype.hasOwnProperty.call(raw, "tags")) {
        return video;
      }
      const seeded = DEFAULT_HOW_TO_VIDEOS.find((item) => item.id === video.id);
      if (seeded) {
        return {
          ...video,
          tags: normalizeHowToTags(seeded.tags),
        };
      }
      return video;
    })
    .filter((video) => video.id);
}

export function embeddableHowToVideos(appSettings = {}) {
  return resolveHowToVideos(appSettings)
    .map((video) => ({
      ...video,
      embedUrl: youtubeEmbedUrl(video.url),
    }))
    .filter((video) => video.embedUrl);
}

export function howToVideoMatchesQuery(video, query) {
  const q = String(query || "")
    .trim()
    .toLowerCase();
  if (!q) {
    return true;
  }
  const haystack = [
    video?.title,
    video?.description,
    placementLabel(video?.placement),
    ...(Array.isArray(video?.tags) ? video.tags : []),
  ]
    .join(" ")
    .toLowerCase();
  if (haystack.includes(q)) {
    return true;
  }
  const singular = q.replace(/s\b/g, "");
  return Boolean(singular) && singular !== q && haystack.includes(singular);
}

export function filterHowToVideos(videos, query) {
  const list = Array.isArray(videos) ? videos : [];
  return list.filter((video) => howToVideoMatchesQuery(video, query));
}

export function trainingUrlFieldsFromHowToVideos(videos = []) {
  const payload = {};
  HOW_TO_VIDEO_PLACEMENTS.forEach((item) => {
    payload[item.settingKey] = "";
  });
  videos.forEach((raw) => {
    const video = normalizeHowToVideo(raw);
    if (!video.placement) {
      return;
    }
    const meta = HOW_TO_VIDEO_PLACEMENTS.find(
      (item) => item.value === video.placement,
    );
    if (meta) {
      payload[meta.settingKey] = video.url;
    }
  });
  return payload;
}

export function validateHowToVideos(videos) {
  const list = Array.isArray(videos) ? videos : [];
  const ids = new Set();
  const placements = new Set();
  for (const raw of list) {
    const video = normalizeHowToVideo(raw);
    if (!video.id) {
      return "Each how-to video needs an ID.";
    }
    if (ids.has(video.id)) {
      return `Duplicate how-to video ID "${video.id}".`;
    }
    ids.add(video.id);
    if (!video.title) {
      return "Each how-to video needs a title.";
    }
    if (video.url && !youtubeVideoId(video.url)) {
      return `"${video.title}" needs a valid YouTube URL.`;
    }
    if (video.placement) {
      if (placements.has(video.placement)) {
        return `Only one video can use the "${placementLabel(
          video.placement,
        )}" in-product slot.`;
      }
      placements.add(video.placement);
    }
  }
  return null;
}
